import { infiniteQueryOptions, useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

import { useGraphQLClient } from "../graphql/client";
import {
  collectionHandleQuery,
  collectionQuery,
  collectionQuickAddQuery,
  collectionsFiltersQuery,
  collectionsQuery,
  collectionsTitleQuery,
} from "../graphql/collection-gql";
import { INFINITE_LIST_QUERY_OPTIONS, NO_CACHE_QUERY_OPTIONS, useCacheFirstRequest } from "../graphql/queryClient";
import type { ICollectionHandleQuery, ICollectionQuery, ICollectionsFiltersQuery, ICollectionsQuery } from "../interface/collection";
import { locale } from "../locales";
import { ToastType } from "../types/layout";
import type { IUseCollectionQuery } from "../types/queries";

function mergeCollectionPages<T extends { collection?: any }>(pages: T[] | undefined): T | undefined {
  if (!pages?.length) return undefined;

  const firstCollection = pages[0]?.collection;
  // collection이 없으면(잘못된 handle 등) 그대로 반환 → 호출부의 `!data.collection` 분기 유지
  if (!firstCollection) return pages[0];

  const lastCollection = pages[pages.length - 1].collection;

  return {
    collection: {
      ...lastCollection,
      products: {
        ...lastCollection.products,
        edges: pages.flatMap((page) => page.collection?.products?.edges ?? []),
        pageInfo: lastCollection.products.pageInfo,
      },
    },
  } as T;
}

/** 컬렉션 조회 (퀵애드/트렌딩/목록/필터/핸들/추천 상품) */
export default function useCollectionQuery({
  lang,
  openToast,
  skipCollectionList,
  skipFilters,
  initialCollectionVariables = null,
  initialCollectionDetailVariables = null,
}: IUseCollectionQuery) {
  const request = useGraphQLClient();
  const cacheFirstRequest = useCacheFirstRequest();
  const queryClient = useQueryClient();

  /** 퀵 애드 컬렉션 조회. main의 cache-first 대응 (staleTime: Infinity → 마운트/인스턴스 간 캐시 공유) */
  const [quickAddVariables, setQuickAddVariables] = useState<Record<string, unknown> | null>(null);

  const quickAddResult = useQuery({
    queryKey: ["shopifyV2", "collection-quickadd", quickAddVariables],
    queryFn: () => request<ICollectionHandleQuery>(collectionQuickAddQuery, quickAddVariables ?? {}),
    enabled: quickAddVariables !== null,
    staleTime: Infinity,
  });

  const fetchQuickAdd = (variables: Record<string, unknown>) => setQuickAddVariables(variables);

  /**
   * 컬렉션 handle 조회 (lazy + 무한스크롤 + await 데이터). cache-first 복원을 위해 staleTime 부여.
   * initialCollectionVariables가 있으면 첫 렌더부터 활성화되어 캐시를 동기적으로 읽는다.
   */
  const [collectionVariables, setCollectionVariables] = useState<Record<string, unknown> | null>(initialCollectionVariables);

  const collectionHandleOptions = (variables: Record<string, unknown> | null) =>
    infiniteQueryOptions({
      ...INFINITE_LIST_QUERY_OPTIONS,
      queryKey: ["shopifyV2", "collection-handle", variables],
      queryFn: ({ pageParam }) => request<ICollectionHandleQuery>(collectionHandleQuery, { ...variables, after: pageParam }),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (lastPage) =>
        lastPage.collection?.products?.pageInfo?.hasNextPage ? (lastPage.collection.products.pageInfo.endCursor ?? undefined) : undefined,
    });

  const collectionInfinite = useInfiniteQuery({
    ...collectionHandleOptions(collectionVariables),
    enabled: collectionVariables !== null,
  });

  const collectionData = useMemo(() => mergeCollectionPages(collectionInfinite.data?.pages), [collectionInfinite.data]);

  /**
   * 조회 시작 + 첫 페이지 데이터 반환.
   * setState로 무한스크롤 observer를 구동하고, 동일 queryKey를 fetchInfiniteQuery로 채워
   * await 호출부(ProductDetailTab 등)가 즉시 데이터를 받도록 한다. (staleTime 덕에 중복 요청 없음)
   */
  const fetchCollection = async (variables: Record<string, unknown>) => {
    setCollectionVariables(variables);
    const result = await queryClient.fetchInfiniteQuery(collectionHandleOptions(variables));
    return { data: mergeCollectionPages(result.pages) };
  };
  const fetchMoreCollection = () => collectionInfinite.fetchNextPage();
  const refetchCollection = async () => {
    const result = await collectionInfinite.refetch();
    return { data: mergeCollectionPages(result.data?.pages) };
  };

  /** 전체 컬렉션 목록 조회 (마운트 시 자동, 내부 전용) */
  const collectionListResult = useQuery({
    queryKey: ["shopifyV2", "collection-list"],
    queryFn: () => request<ICollectionsQuery>(collectionsQuery),
    enabled: !skipCollectionList,
    staleTime: Infinity, // main의 기본 cache-first 대응 (마운트마다 재요청하지 않음)
  });

  /** 컬렉션 필터 조회 (마운트 시 자동, 내부 전용). no-cache → staleTime 0 */
  const collectionFiltersResult = useQuery({
    queryKey: ["shopifyV2", "collection-filters", { first: 10 }],
    queryFn: () => request<ICollectionsFiltersQuery>(collectionsFiltersQuery, { first: 10 }),
    enabled: !skipFilters,
    ...NO_CACHE_QUERY_OPTIONS,
  });

  /** 전체 컬렉션 타이틀 조회 (명령형) */
  const allCollectionMutation = useMutation({
    mutationFn: (variables: Record<string, unknown>) =>
      cacheFirstRequest<ICollectionsQuery>("collections-title", collectionsTitleQuery, variables),
    onError: (error) => {
      openToast({ message: locale(lang).error.server, type: ToastType.Error });
      throw new Error(error.message);
    },
  });

  /** 단일 컬렉션 상세 조회 (lazy + 무한스크롤) */
  const [collectionDetailVariables, setCollectionDetailVariables] = useState<Record<string, unknown> | null>(
    initialCollectionDetailVariables,
  );

  const collectionDetailInfinite = useInfiniteQuery({
    ...INFINITE_LIST_QUERY_OPTIONS,
    queryKey: ["shopifyV2", "collection-detail", collectionDetailVariables],
    enabled: collectionDetailVariables !== null,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => request<ICollectionQuery>(collectionQuery, { ...collectionDetailVariables, after: pageParam }),
    getNextPageParam: (lastPage) =>
      lastPage.collection?.products?.pageInfo?.hasNextPage ? (lastPage.collection.products.pageInfo.endCursor ?? undefined) : undefined,
  });

  const collectionDetailData = useMemo(() => mergeCollectionPages(collectionDetailInfinite.data?.pages), [collectionDetailInfinite.data]);

  const fetchCollectionDetail = (variables: Record<string, unknown>) => {
    setCollectionDetailVariables(variables);
  };

  const fetchMoreCollectionDetail = () => collectionDetailInfinite.fetchNextPage();

  // useQuery/useInfiniteQuery는 v5에서 onError가 없으므로 에러 토스트는 effect로 처리
  useEffect(() => {
    if (
      quickAddResult.error ||
      collectionInfinite.error ||
      collectionDetailInfinite.error ||
      collectionListResult.error ||
      collectionFiltersResult.error
    ) {
      openToast({ message: locale(lang).error.server, type: ToastType.Error });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    quickAddResult.error,
    collectionInfinite.error,
    collectionDetailInfinite.error,
    collectionListResult.error,
    collectionFiltersResult.error,
  ]);

  return {
    fetchQuickAdd,
    quickAddData: quickAddResult.data,
    quickAddLoading: quickAddResult.isFetching,
    collectionListData: collectionListResult.data,
    collectionListLoading: collectionListResult.isFetching,
    collectionFilters: collectionFiltersResult.data,
    fetchCollection,
    collectionData,
    collectionLoading: collectionInfinite.isFetching,
    fetchMoreCollection,
    refetchCollection,
    fetchAllCollection: allCollectionMutation.mutateAsync,
    allCollectionData: allCollectionMutation.data,
    allCollectionLoading: allCollectionMutation.isPending,
    fetchCollectionDetail,
    collectionDetailData,
    collectionDetailLoading: collectionDetailInfinite.isFetching,
    fetchMoreCollectionDetail,
  };
}
